/**
 * LivePreviewPlugin — Obsidian-style live preview
 *
 * Implements Obsidian's "Live Preview" behaviour: when the cursor is inside a
 * block, that block shows raw markdown syntax; all other blocks show rendered
 * output. Implemented entirely via DOM data-attributes + CSS — no node
 * manipulation, no undo/redo side effects.
 *
 * How it works:
 *   1. registerUpdateListener fires after every Lexical state change.
 *   2. We read the focused block key from the current selection.
 *   3. We imperatively set data-lp-focused="true|false" on every block's DOM
 *      element via editor.getElementByKey().
 *   4. CSS in WorkingDocEditor.module.css handles the visual switch:
 *      - data-lp-focused="true"  → show ::before/::after markdown markers,
 *                                   reset rendered styles (font-size, etc.)
 *      - data-lp-focused="false" → hide markers, apply rendered styles
 *
 * Special cases:
 *   - ListNode: sets data-lp-focused on each ListItemNode child (not the
 *     ListNode itself), because only the active list item shows source mode.
 *     Nested lists are handled recursively.
 *   - DecoratorNode (CodeBlockDecoratorNode): skipped — code blocks have their
 *     own live preview (textarea toggle on click).
 */

import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, $isDecoratorNode, $isRangeSelection } from 'lexical';
import type { LexicalEditor } from 'lexical';
import { $isListNode, $isListItemNode, ListItemNode } from '@lexical/list';
import type { ListNode } from '@lexical/list';
import { $getNearestNodeOfType } from '@lexical/utils';

// ─── Helper: apply data-lp-focused to all items in a ListNode (recursive) ─────

function markListChildren(
  listNode: ListNode,
  focusedKey: string | null,
  editor: LexicalEditor,
): void {
  for (const item of listNode.getChildren()) {
    if (!$isListItemNode(item)) continue;

    const el = editor.getElementByKey(item.getKey());
    if (el instanceof HTMLElement) {
      el.dataset.lpFocused = String(item.getKey() === focusedKey);
    }

    // Recurse into nested lists (e.g. indented list items)
    for (const child of item.getChildren()) {
      if ($isListNode(child)) {
        markListChildren(child, focusedKey, editor);
      }
    }
  }
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export function LivePreviewPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        // 1. Resolve which "block" the cursor is in.
        //    For list items we want the ListItemNode (not the parent ListNode)
        //    so that only the active item shows source mode.
        let focusedKey: string | null = null;
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const anchor = selection.anchor.getNode();
          try {
            const topLevel = anchor.getTopLevelElementOrThrow();
            if ($isListNode(topLevel)) {
              const item = $getNearestNodeOfType(anchor, ListItemNode);
              focusedKey = item?.getKey() ?? null;
            } else {
              focusedKey = topLevel.getKey();
            }
          } catch {
            // anchor is root (empty editor) — focusedKey stays null
          }
        }

        // 2. Walk all top-level blocks and stamp data-lp-focused.
        for (const child of $getRoot().getChildren()) {
          // DecoratorNodes (code blocks) manage their own live preview.
          if ($isDecoratorNode(child)) continue;

          if ($isListNode(child)) {
            // Don't mark the ListNode itself — mark each ListItemNode.
            const listEl = editor.getElementByKey(child.getKey());
            if (listEl instanceof HTMLElement) {
              listEl.removeAttribute('data-lp-focused');
            }
            markListChildren(child, focusedKey, editor);
          } else {
            const el = editor.getElementByKey(child.getKey());
            if (el instanceof HTMLElement) {
              el.dataset.lpFocused = String(child.getKey() === focusedKey);
            }
          }
        }
      });
    });
  }, [editor]);

  return null;
}
